<template>
  <div class="bg-white shadow-sm rounded-lg">
    <div class="px-6 py-4 border-b border-gray-200">
      <h2 class="text-lg font-medium text-gray-900">
        UTM Attribution Analysis
      </h2>
      <p class="mt-1 text-sm text-gray-500">
        Distribution of contacts by UTM parameters, segmented by contact type
      </p>
    </div>

    <div class="px-6 py-4">
      <div
        v-for="connection in connectionsWithData"
        :key="connection.id"
        class="mb-12">
        <h3 class="text-md font-medium text-gray-900 mb-2">
          {{ getInstanceLabel(connection) }} Instance - {{ connection.location_id }}
        </h3>
        <div class="text-sm text-gray-600 mb-6">
          Lead Contacts: {{ getContactCounts(connection.id, 'lead') }} |
          Support Contacts: {{ getContactCounts(connection.id, 'support') }}
        </div>

        <!-- Segmented Charts by Contact Type -->
        <div
          v-for="contactType in ['lead', 'support']"
          :key="`${connection.id}-${contactType}`"
          class="mb-8">
          <div v-if="hasDataForType(connection.id, contactType)">
            <h4 class="text-sm font-semibold text-gray-800 mb-4 flex items-center">
              <span
                class="inline-block w-3 h-3 rounded-full mr-2"
                :class="contactType === 'lead' ? 'bg-blue-500' : 'bg-green-500'" />
              {{ contactType === 'lead' ? 'Lead' : 'Support' }} Contacts Attribution
              <span class="ml-2 text-xs text-gray-500 font-normal">
                ({{ getAttributionPercentage(connection.id, contactType) }}% with attribution)
              </span>
            </h4>

            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <!-- Lead contacts have different attribution fields -->
              <template v-if="contactType === 'lead'">
                <div
                  v-if="hasData(connection.id, contactType, 'source')"
                  class="bg-gray-50 p-4 rounded-lg">
                  <h5 class="text-xs font-medium text-gray-700 mb-2">
                    Source
                  </h5>
                  <canvas
                    :data-chart-id="`source-${connection.id}-${contactType}`"
                    width="250"
                    height="250" />
                </div>

                <div
                  v-if="hasData(connection.id, contactType, 'medium')"
                  class="bg-gray-50 p-4 rounded-lg">
                  <h5 class="text-xs font-medium text-gray-700 mb-2">
                    Medium
                  </h5>
                  <canvas
                    :data-chart-id="`medium-${connection.id}-${contactType}`"
                    width="250"
                    height="250" />
                </div>

                <div
                  v-if="hasData(connection.id, contactType, 'session_source')"
                  class="bg-gray-50 p-4 rounded-lg">
                  <h5 class="text-xs font-medium text-gray-700 mb-2">
                    Session Source
                  </h5>
                  <canvas
                    :data-chart-id="`session-source-${connection.id}-${contactType}`"
                    width="250"
                    height="250" />
                </div>
              </template>

              <!-- Support contacts have UTM fields -->
              <template v-else>
                <div
                  v-if="hasData(connection.id, contactType, 'utm_content')"
                  class="bg-gray-50 p-4 rounded-lg">
                  <h5 class="text-xs font-medium text-gray-700 mb-2">
                    UTM Content
                  </h5>
                  <canvas
                    :data-chart-id="`utm-content-${connection.id}-${contactType}`"
                    width="250"
                    height="250" />
                </div>

                <div
                  v-if="hasData(connection.id, contactType, 'utm_medium')"
                  class="bg-gray-50 p-4 rounded-lg">
                  <h5 class="text-xs font-medium text-gray-700 mb-2">
                    UTM Medium
                  </h5>
                  <canvas
                    :data-chart-id="`utm-medium-${connection.id}-${contactType}`"
                    width="250"
                    height="250" />
                </div>

                <div
                  v-if="hasData(connection.id, contactType, 'utm_campaign')"
                  class="bg-gray-50 p-4 rounded-lg">
                  <h5 class="text-xs font-medium text-gray-700 mb-2">
                    UTM Campaign
                  </h5>
                  <canvas
                    :data-chart-id="`utm-campaign-${connection.id}-${contactType}`"
                    width="250"
                    height="250" />
                </div>

                <div
                  v-if="hasData(connection.id, contactType, 'utm_source')"
                  class="bg-gray-50 p-4 rounded-lg">
                  <h5 class="text-xs font-medium text-gray-700 mb-2">
                    UTM Source
                  </h5>
                  <canvas
                    :data-chart-id="`utm-source-${connection.id}-${contactType}`"
                    width="250"
                    height="250" />
                </div>
              </template>
            </div>

            <!-- No data message for contact type -->
            <div
              v-if="!hasDataForType(connection.id, contactType)"
              class="text-center py-4 text-gray-500 text-sm bg-gray-50 rounded-lg">
              No {{ contactType === 'lead' ? 'attribution' : 'UTM attribution' }} data available for {{ contactType }} contacts
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!connectionsWithData.length"
        class="text-center py-8 text-gray-500">
        No UTM attribution data available. Run the attribution sync to populate data.
      </div>
    </div>

    <!-- Detailed Analysis Section -->
    <div
      v-if="connectionsWithData.length > 0"
      class="border-t border-gray-200 px-6 py-4">
      <h3 class="text-lg font-medium text-gray-900 mb-4">
        Detailed Attribution Analysis by Contact Type
      </h3>

      <div
        v-for="connection in connectionsWithData"
        :key="`analysis-${connection.id}`"
        class="mb-8">
        <h4 class="text-md font-medium text-gray-900 mb-4">
          {{ getInstanceLabel(connection) }} Instance
        </h4>

        <!-- Show data for the contact type that has data -->
        <div v-if="hasDataForConnection(connection.id)">
          <!-- Determine which contact type to show based on what data exists -->
          <div
            v-for="contactType in ['lead', 'support']"
            v-show="hasDataForType(connection.id, contactType)"
            :key="`content-${connection.id}-${contactType}`">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <!-- Lead contacts show different attribution data -->
              <template v-if="contactType === 'lead'">
                <!-- Medium Table for Lead Contacts -->
                <div
                  v-if="hasData(connection.id, contactType, 'medium')"
                  class="bg-gray-50 rounded-lg p-4">
                  <h5 class="text-sm font-semibold text-gray-700 mb-3">
                    Top Medium Types
                  </h5>
                  <div class="overflow-x-auto">
                    <table class="min-w-full">
                      <thead>
                        <tr class="border-b border-gray-200">
                          <th class="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                            Medium
                          </th>
                          <th class="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                            Contacts
                          </th>
                          <th class="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                            %
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, index) in getTopItems(connection.id, contactType, 'medium', 10)"
                          :key="`medium-${index}`"
                          class="border-b border-gray-100">
                          <td class="py-2 text-sm text-gray-900">
                            {{ item.label }}
                          </td>
                          <td class="py-2 text-sm text-gray-900 text-right">
                            {{ item.value }}
                          </td>
                          <td class="py-2 text-sm text-gray-500 text-right">
                            {{ item.percentage }}%
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <!-- Session Source Table for Lead Contacts -->
                <div
                  v-if="hasData(connection.id, contactType, 'session_source')"
                  class="bg-gray-50 rounded-lg p-4">
                  <h5 class="text-sm font-semibold text-gray-700 mb-3">
                    Top Session Sources
                  </h5>
                  <div class="overflow-x-auto">
                    <table class="min-w-full">
                      <thead>
                        <tr class="border-b border-gray-200">
                          <th class="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                            Session Source
                          </th>
                          <th class="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                            Contacts
                          </th>
                          <th class="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                            %
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, index) in getTopItems(connection.id, contactType, 'session_source', 10)"
                          :key="`session-${index}`"
                          class="border-b border-gray-100">
                          <td class="py-2 text-sm text-gray-900">
                            {{ item.label }}
                          </td>
                          <td class="py-2 text-sm text-gray-900 text-right">
                            {{ item.value }}
                          </td>
                          <td class="py-2 text-sm text-gray-500 text-right">
                            {{ item.percentage }}%
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </template>

              <!-- Support contacts show UTM data -->
              <template v-else>
                <!-- UTM Content Table -->
                <div
                  v-if="hasData(connection.id, contactType, 'utm_content')"
                  class="bg-gray-50 rounded-lg p-4">
                  <h5 class="text-sm font-semibold text-gray-700 mb-3">
                    Top UTM Content
                  </h5>
                  <div class="overflow-x-auto">
                    <table class="min-w-full">
                      <thead>
                        <tr class="border-b border-gray-200">
                          <th class="text-left text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                            Content
                          </th>
                          <th class="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                            Contacts
                          </th>
                          <th class="text-right text-xs font-medium text-gray-500 uppercase tracking-wider pb-2">
                            %
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="(item, index) in getTopItems(connection.id, contactType, 'utm_content', 10)"
                          :key="`content-${index}`"
                          class="border-b border-gray-100">
                          <td class="py-2 text-sm text-gray-900">
                            {{ truncateText(item.label, 40) }}
                          </td>
                          <td class="py-2 text-sm text-gray-900 text-right">
                            {{ item.value }}
                          </td>
                          <td class="py-2 text-sm text-gray-500 text-right">
                            {{ item.percentage }}%
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    <div
                      v-if="getOthersCount(connection.id, contactType, 'utm_content', 10) > 0"
                      class="mt-2 text-xs text-gray-500">
                      + {{ getOthersCount(connection.id, contactType, 'utm_content', 10) }} other content variations
                    </div>
                  </div>
                </div>

                <!-- Campaign Performance Summary -->
                <div class="bg-gray-50 rounded-lg p-4">
                  <h5 class="text-sm font-semibold text-gray-700 mb-3">
                    Campaign Summary
                  </h5>
                  <div class="space-y-3">
                    <div v-if="hasData(connection.id, contactType, 'utm_campaign')">
                      <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">
                        Top Campaign
                      </p>
                      <p class="text-sm font-medium text-gray-900">
                        {{ getTopItem(connection.id, contactType, 'utm_campaign') }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ getTopItemStats(connection.id, contactType, 'utm_campaign') }}
                      </p>
                    </div>

                    <div v-if="hasData(connection.id, contactType, 'utm_medium')">
                      <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">
                        Top Medium
                      </p>
                      <p class="text-sm font-medium text-gray-900">
                        {{ getTopItem(connection.id, contactType, 'utm_medium') }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ getTopItemStats(connection.id, contactType, 'utm_medium') }}
                      </p>
                    </div>

                    <div v-if="hasData(connection.id, contactType, 'utm_source')">
                      <p class="text-xs text-gray-500 uppercase tracking-wider mb-1">
                        Top Source
                      </p>
                      <p class="text-sm font-medium text-gray-900">
                        {{ getTopItem(connection.id, contactType, 'utm_source') }}
                      </p>
                      <p class="text-xs text-gray-500">
                        {{ getTopItemStats(connection.id, contactType, 'utm_source') }}
                      </p>
                    </div>
                  </div>
                </div>
              </template>
            </div>

            <!-- Bar Chart for UTM Content Distribution -->
            <div
              v-if="hasData(connection.id, contactType, 'utm_content')"
              class="mt-4 bg-gray-50 rounded-lg p-4">
              <h5 class="text-sm font-semibold text-gray-700 mb-3">
                Content Performance Distribution
              </h5>
              <div style="height: 300px;">
                <canvas :data-chart-id="`bar-utm-content-${connection.id}-${contactType}`" />
              </div>
            </div>
          </div>
        </div>

        <!-- Show message if no data -->
        <div
          v-else
          class="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
          No attribution data available for this connection
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, onMounted, computed, nextTick
} from 'vue'
import { Chart, registerables } from 'chart.js'

Chart.register(...registerables)

const props = defineProps({
  connections: {
    type: Array,
    required: true
  },
  utmData: {
    type: Object,
    required: true
  }
})

const chartInstances = ref({})

// Chart colors
const chartColors = [
  '#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6',
  '#ec4899', '#14b8a6', '#f97316', '#06b6d4', '#84cc16',
  '#a855f7', '#f43f5e', '#22c55e', '#eab308', '#6366f1'
]

// Swap the labels as requested
const getInstanceLabel = (connection) => (connection.instance_type === 'lead' ? 'Support' : 'Lead')

const hasDataForType = (connectionId, contactType) => {
  const data = props.utmData[connectionId]?.[contactType]
  if (!data) return false

  // Lead contacts have different fields
  if (contactType === 'lead') {
    return (
      (data.source && Object.keys(data.source).length > 0) ||
      (data.medium && Object.keys(data.medium).length > 0) ||
      (data.session_source && Object.keys(data.session_source).length > 0) ||
      (data.utm_content && Object.keys(data.utm_content).length > 0) ||
      (data.utm_medium && Object.keys(data.utm_medium).length > 0) ||
      (data.utm_campaign && Object.keys(data.utm_campaign).length > 0) ||
      (data.utm_source && Object.keys(data.utm_source).length > 0)
    )
  }

  // Support contacts have UTM fields
  return (
    (data.utm_content && Object.keys(data.utm_content).length > 0) ||
    (data.utm_medium && Object.keys(data.utm_medium).length > 0) ||
    (data.utm_campaign && Object.keys(data.utm_campaign).length > 0) ||
    (data.utm_source && Object.keys(data.utm_source).length > 0)
  )
}

const hasDataForConnection = (connectionId) => hasDataForType(connectionId, 'lead') || hasDataForType(connectionId, 'support')

const connectionsWithData = computed(() => {
  if (!props.connections || !props.utmData) return []

  return props.connections.filter((connection) => {
    const data = props.utmData[connection.id]
    return data && (hasDataForType(connection.id, 'lead') || hasDataForType(connection.id, 'support'))
  })
})

const hasData = (connectionId, contactType, field) => {
  const data = props.utmData[connectionId]?.[contactType]?.[field]
  return data && Object.keys(data).length > 0
}

const getContactCounts = (connectionId, contactType) => {
  const data = props.utmData[connectionId]?.[contactType]
  if (!data) return '0'

  const withAttribution = data.total_with_attribution || 0
  const total = data.total || 0

  return `${withAttribution}/${total}`
}

const getAttributionPercentage = (connectionId, contactType) => {
  const data = props.utmData[connectionId]?.[contactType]
  if (!data || !data.total || data.total === 0) return '0'

  const percentage = ((data.total_with_attribution || 0) / data.total * 100).toFixed(1)
  return percentage
}

const createPieChart = (canvas, data, colors) => {
  if (!canvas) return null

  const ctx = canvas.getContext('2d')
  const labels = Object.keys(data)
  const values = Object.values(data)

  return new Chart(ctx, {
    type: 'pie',
    data: {
      labels: labels,
      datasets: [{
        data: values,
        backgroundColor: colors.slice(0, labels.length),
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: true,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            padding: 8,
            font: {
              size: 10
            }
          }
        },
        tooltip: {
          callbacks: {
            label: function tooltipLabel(context) {
              const label = context.label || ''
              const value = context.parsed || 0
              const total = context.dataset.data.reduce((a, b) => a + b, 0)
              const percentage = ((value / total) * 100).toFixed(1)
              return `${label}: ${value} (${percentage}%)`
            }
          }
        }
      }
    }
  })
}

const getTopItems = (connectionId, contactType, field, limit = 10) => {
  const data = props.utmData[connectionId]?.[contactType]?.[field] || {}
  const total = Object.values(data).reduce((sum, val) => sum + val, 0)

  return Object.entries(data)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)
    .map(([label, value]) => ({
      label,
      value,
      percentage: ((value / total) * 100).toFixed(1)
    }))
}

const getOthersCount = (connectionId, contactType, field, limit = 10) => {
  const data = props.utmData[connectionId]?.[contactType]?.[field] || {}
  const totalItems = Object.keys(data).length
  return Math.max(0, totalItems - limit)
}

const getTopItem = (connectionId, contactType, field) => {
  const items = getTopItems(connectionId, contactType, field, 1)
  return items[0]?.label || 'N/A'
}

const getTopItemStats = (connectionId, contactType, field) => {
  const items = getTopItems(connectionId, contactType, field, 1)
  if (items[0]) {
    return `${items[0].value} contacts (${items[0].percentage}%)`
  }
  return ''
}

const truncateText = (text, maxLength = 40) => {
  if (!text || text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}

const createBarChart = (canvas, data, color, limit = 15) => {
  if (!canvas) return null

  const ctx = canvas.getContext('2d')

  // Sort and limit data
  const sortedData = Object.entries(data)
    .sort(([, a], [, b]) => b - a)
    .slice(0, limit)

  const labels = sortedData.map(([label]) => truncateText(label, 30))
  const values = sortedData.map(([, value]) => value)

  return new Chart(ctx, {
    type: 'bar',
    data: {
      labels: labels,
      datasets: [{
        label: 'Number of Contacts',
        data: values,
        backgroundColor: color,
        borderColor: color,
        borderWidth: 1
      }]
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      indexAxis: 'y', // Horizontal bars
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function tooltipLabel(context) {
              const value = context.parsed.x || 0
              const total = values.reduce((a, b) => a + b, 0)
              const percentage = ((value / total) * 100).toFixed(1)
              return `${value} contacts (${percentage}%)`
            }
          }
        }
      },
      scales: {
        x: {
          beginAtZero: true,
          grid: {
            display: true,
            color: 'rgba(0, 0, 0, 0.05)'
          }
        },
        y: {
          grid: {
            display: false
          },
          ticks: {
            autoSkip: false
          }
        }
      }
    }
  })
}

const renderCharts = async() => {
  await nextTick()

  // Destroy existing charts
  Object.values(chartInstances.value).forEach((chart) => {
    if (chart) chart.destroy()
  })
  chartInstances.value = {}

  // Ensure we have data before proceeding
  if (!props.connections || !props.utmData) {
    return
  }

  // Create new charts for each connection and contact type
  connectionsWithData.value.forEach((connection) => {
    const data = props.utmData[connection.id]

    // Check if data exists and has the expected structure
    if (!data) {
      // Silent fail - no UTM data for this connection
      return
    }

    // Process each contact type
    ['lead', 'support'].forEach((contactType) => {
      const typeData = data[contactType]
      if (!typeData) return

      // const colors = contactType === 'lead' ? leadColors : supportColors
      // Note: colors variable removed as it's not used in this section

      // Lead contacts have different fields
      if (contactType === 'lead') {
        // Source chart
        if (typeData.source && Object.keys(typeData.source).length > 0) {
          const canvasId = `source-${connection.id}-${contactType}`
          const canvas = document.querySelector(`[data-chart-id="${canvasId}"]`)
          if (canvas) {
            chartInstances.value[canvasId] = createPieChart(canvas, typeData.source, chartColors)
          }
        }

        // Medium chart
        if (typeData.medium && Object.keys(typeData.medium).length > 0) {
          const canvasId = `medium-${connection.id}-${contactType}`
          const canvas = document.querySelector(`[data-chart-id="${canvasId}"]`)
          if (canvas) {
            chartInstances.value[canvasId] = createPieChart(canvas, typeData.medium, chartColors)
          }
        }

        // Session Source chart
        if (typeData.session_source && Object.keys(typeData.session_source).length > 0) {
          const canvasId = `session-source-${connection.id}-${contactType}`
          const canvas = document.querySelector(`[data-chart-id="${canvasId}"]`)
          if (canvas) {
            chartInstances.value[canvasId] = createPieChart(canvas, typeData.session_source, chartColors)
          }
        }
      }

      // Pie Charts - Only show top 8 items for UTM Content to avoid clutter
      if (typeData.utm_content && Object.keys(typeData.utm_content).length > 0) {
        const canvasId = `utm-content-${connection.id}-${contactType}`
        const canvas = document.querySelector(`[data-chart-id="${canvasId}"]`)
        if (canvas) {
          // Limit pie chart to top 8 items and group the rest as "Others"
          const limitedData = {}
          const sortedEntries = Object.entries(typeData.utm_content).sort(([, a], [, b]) => b - a)
          let othersCount = 0

          sortedEntries.forEach(([label, value], index) => {
            if (index < 8) {
              limitedData[label] = value
            } else {
              othersCount += value
            }
          })

          if (othersCount > 0) {
            limitedData.Others = othersCount
          }

          chartInstances.value[canvasId] = createPieChart(canvas, limitedData, chartColors)
        }

        // Bar chart for detailed view
        const barCanvasId = `bar-utm-content-${connection.id}-${contactType}`
        const barCanvas = document.querySelector(`[data-chart-id="${barCanvasId}"]`)
        if (barCanvas) {
          const barColor = contactType === 'lead' ? '#3b82f6' : '#10b981'
          chartInstances.value[barCanvasId] = createBarChart(barCanvas, typeData.utm_content, barColor)
        }
      }

      // UTM Medium
      if (typeData.utm_medium && Object.keys(typeData.utm_medium).length > 0) {
        const canvasId = `utm-medium-${connection.id}-${contactType}`
        const canvas = document.querySelector(`[data-chart-id="${canvasId}"]`)
        if (canvas) {
          chartInstances.value[canvasId] = createPieChart(canvas, typeData.utm_medium, chartColors)
        }
      }

      // UTM Campaign
      if (typeData.utm_campaign && Object.keys(typeData.utm_campaign).length > 0) {
        const canvasId = `utm-campaign-${connection.id}-${contactType}`
        const canvas = document.querySelector(`[data-chart-id="${canvasId}"]`)
        if (canvas) {
          chartInstances.value[canvasId] = createPieChart(canvas, typeData.utm_campaign, chartColors)
        }
      }

      // UTM Source
      if (typeData.utm_source && Object.keys(typeData.utm_source).length > 0) {
        const canvasId = `utm-source-${connection.id}-${contactType}`
        const canvas = document.querySelector(`[data-chart-id="${canvasId}"]`)
        if (canvas) {
          chartInstances.value[canvasId] = createPieChart(canvas, typeData.utm_source, chartColors)
        }
      }
    })
  })
}

onMounted(() => {
  // Debug logging - uncomment if needed
  // console.log('UTM Data structure:', props.utmData)
  // console.log('Connections:', props.connections)
  renderCharts()
})
</script>
